import { AddRounded, KeyboardArrowDownRounded } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Modal,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from "@mui/material";
import moment from "moment";
import { useEffect, useState } from "react";
import { DATETIME_FORMAT2 } from "../../../../config/constants";
import { useApi } from "../../../../hooks/useApi.hook";
import { IDocument, IDocumentType, IReceiver } from "../../../../models/docs";
import DocsService from "../../../../services/api/docs.service";
import { Wrapper } from "./styles";
import IconDelete from "../../../../assets/images/common/trash_bin.svg";
import IconEdit from "../../../../assets/images/common/document_edit.svg";
import IconInfo from "../../../../assets/images/common/info_square.svg";
import EmptyProject from "../../../../assets/images/common/empty_project.svg";
import { ImageCustom } from "../../../../components";
import {
  documentStatusColor,
  documentStatusTitle
} from "../../../../models/common/models.enum";
import DocsContent from "../DocsContent/DocsContent";

import CreateDocumentPopup from "../CreateDocumentPopup";
import { IAttachmentFile } from "../../../../models/Task";
import UploadFileService from "../../../../services/api/uploadFile.service";
import { toggleMessage } from "../../../../components/Toast/Toast";
import { toggleConfirmModal } from "../../../../components/ConfirmModal/ConfirmModal";

import CreateDocumentTypePopup from "../CreateDocumentTypePopup";
import DocumentTypePopup from "../DocumentTypePopup";
import { jwtDecode } from "jwt-decode";
import { ITokenPayload } from "@/models/user";
import { useTranslation } from "react-i18next";

const style = {
  position: "absolute" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "100vw",
  minHeight: "100vh",
  height: "100vh",
  alignContent: "center",
  overflow: "auto",
  backgroundColor: "transparent"
};

const boxStyle = {
  backgroundColor: "#f1f5f6",
  padding: "14px",
  position: "absolute" as const,
  top: "50%",
  left: "50%",
  height: "auto",
  transform: "translate(-50%, -50%)",
  width: "40vw",
  borderRadius: "8px"
};

type Props = {
  isCreate: boolean;
  isUpdate: boolean;
  isDelete: boolean;
  isCreateType: boolean;
  isViewType: boolean;
  isUpdateType: boolean;
  isDeleteType: boolean;
};

const DocsAdminView = ({
  isCreate,
  isDelete,
  isUpdate,
  isCreateType,
  isUpdateType,
  isDeleteType,
  isViewType
}: Props) => {
  const [open, setOpen] = useState(false);
  const [openCreate, setOpenCreate] = useState(false);
  const [openDocType, setOpenDocType] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [documentTypes, setDocumentTypes] = useState<IDocumentType[] | null>(
    null
  );

  const getPage = useApi<IDocument[]>({ isFetch: true, isPaging: true });
  const getSingle = useApi<IDocument>({ isFetch: true });
  const uploadFile = useApi<IAttachmentFile[]>({});
  const create = useApi<IDocument>({});
  const update = useApi<IDocument>({});
  const deleteDoc = useApi<IDocument>({});
  const getDocumentType = useApi<IDocumentType[]>({ isFetch: true });
  const getDocumentTypeNotPermission = useApi<IDocumentType[]>({
    isFetch: true
  });
  const [token, setToken] = useState<ITokenPayload>();
  const { t } = useTranslation();

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      const decoded = jwtDecode<ITokenPayload>(token);
      setToken(decoded);
    }
  }, []);

  async function fetchDocumentType() {
    await DocsService.getDocumentType({
      CurrentPage: 1,
      PageSize: 10
    }).then(res => {
      if (res.success) setDocumentTypes(res.items);
    });
  }

  async function fetchDocumentTypeNotPermission() {
    await DocsService.getDocumentTypeNotPermission({
      CurrentPage: 1,
      PageSize: 10
    }).then(res => {
      if (res.success) setDocumentTypes(res.items);
    });
  }

  const handleOpen = async (id: string) => {
    getSingle.request(DocsService.getSingle(id));
    await fetchDocumentType();
    setOpen(true);
  };

  const handleCreateOpen = () => {
    if (!isCreateType && documentTypes && documentTypes?.length === 0) {
      toggleMessage({
        type: "error",
        message: t("Docs.Admin.ErrorNoDocTypeNoPermission")
      });
    } else {
      setOpenCreate(true);
    }
  };

  function fetchData(pageIndex?: number) {
    getPage.request(
      DocsService.getPageByAdmin({
        CurrentPage: pageIndex ?? 1,
        PageSize: 10
      })
    );
  }

  function refreshDocumentType() {
    if (isViewType) {
      fetchDocumentType();
    } else {
      fetchDocumentTypeNotPermission();
    }
  }

  useEffect(() => {
    fetchData();
    refreshDocumentType();
  }, []);

  function onCreate(request: any) {
    if (request.reciver) {
      const list = (request.reciver as any[]).map(
        r =>
          ({
            id: "idEdit" in r ? r.idEdit : null,
            userOrDepartmentId: r.id,
            type: r.type,
            userName: r.userName
          }) as IReceiver
      );
      request = { ...request, reciver: list };
    }

    isEdit
      ? update.request(
          DocsService.update(request).then(res => {
            if (res.success) {
              toggleMessage({
                type: "success",
                message: res?.message ?? t("Docs.Admin.UpdateSuccess")
              });
              fetchData(getPage.pagging?.currentPage);
              setOpen(false);
            } else {
              if (request.attachmentFiles) {
                uploadFile.request(
                  UploadFileService.deleteFiles(request.attachmentFiles)
                );
              }
            }
          })
        )
      : create.request(
          DocsService.create(request).then(res => {
            if (res.success) {
              toggleMessage({
                type: "success",
                message: res?.message ?? t("Docs.Admin.CreateSuccess")
              });
              fetchData();
              setOpenCreate(false);
            } else {
              if (request.attachmentFiles) {
                uploadFile.request(
                  UploadFileService.deleteFiles(request.attachmentFiles)
                );
              }
            }
          })
        );
  }

  const createOnSubmit = (request: any) => {
    let files: File[] = [];
    let oldFiles: IAttachmentFile[] = [];

    if (request.attachmentFiles) {
      request.attachmentFiles.map((f: any) =>
        f instanceof File ? files.push(f) : oldFiles.push(f)
      );
      if (files.length) {
        const filesData = new FormData();
        filesData.append("FolderName", "Document_" + token?.connectString);
        files
          .filter(e => e.size > 0)
          .forEach(file => filesData.append("Files", file));
        uploadFile.request(
          UploadFileService.uploadFile(filesData).then(response => {
            if (response.success && response.data) {
              const req = {
                ...request,
                attachmentFiles: [...oldFiles, ...response.data]
              };
              onCreate(req);
            } else {
              toggleMessage({
                type: "error",
                message: t("Docs.Admin.UploadError")
              });
            }
          })
        );
      } else {
        onCreate({ ...request, attachmentFiles: oldFiles });
      }
    } else {
      onCreate(request);
    }
  };

  const handleCloseDocumentType = (isChange: boolean) => {
    setOpenDocType(false);
    if (isChange) {
      refreshDocumentType();
    }
  };

  const handleCloseCreateDocumentType = (isChange: boolean) => {
    setOpenCreate(false);
    if (isChange) {
      refreshDocumentType();
    }
  };

  return (
    <Wrapper>
      {getPage?.data?.length ? (
        <Box>
          <Box className="header-box">
            {isViewType && (
              <Button
                variant="outlined"
                color="info"
                size="small"
                sx={{ borderRadius: "8px" }}
                endIcon={<KeyboardArrowDownRounded />}
                onClick={() => setOpenDocType(true)}
              >
                {t("Docs.Admin.ButtonDocumentType")}
              </Button>
            )}
            {isCreate && (
              <Button
                startIcon={<AddRounded />}
                variant="contained"
                size="small"
                sx={{ borderRadius: "8px", marginLeft: "12px" }}
                onClick={() => handleCreateOpen()}
              >
                {t("Docs.Admin.ButtonCreateDocument")}
              </Button>
            )}
          </Box>

          <Table>
            <TableHead>
              <TableRow>
                <TableCell width={"36%"}>
                  {t("Docs.Admin.TableHeaderDocument")}
                </TableCell>
                <TableCell width={"14%"}>
                  {t("Docs.Admin.TableHeaderStatus")}
                </TableCell>
                <TableCell width={"18%"}>
                  {t("Docs.Admin.TableHeaderType")}
                </TableCell>
                <TableCell width={"17%"}>
                  {t("Docs.Admin.TableHeaderTime")}
                </TableCell>
                <TableCell width={"15%"}></TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {getPage?.data.map(item => (
                <TableRow key={item.id}>
                  <TableCell>
                    <Typography variant="subtitle2" className="text-overflow">
                      [{item.numberIdentifier}] {item.title}
                    </Typography>
                    <div
                      className="text-overflow"
                      dangerouslySetInnerHTML={{
                        __html: item.description.length ? item.description : ""
                      }}
                    ></div>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        backgroundColor: documentStatusColor(item.status).color,
                        color: "white",
                        padding: "4px 10px",
                        borderRadius: "8px",
                        textAlign: "center"
                      }}
                    >
                      {t(documentStatusTitle(item.status))}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2">
                      {item.documentType?.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {t("Docs.Admin.LabelIssueDate")}
                    </Typography>
                    <Typography variant="subtitle2">
                      {item.dateOfIssue
                        ? moment(item.dateOfIssue).format(DATETIME_FORMAT2)
                        : t("Docs.Admin.NoInfo")}
                    </Typography>
                    <Typography variant="body2">
                      {t("Docs.Admin.LabelExpireDate")}
                    </Typography>
                    <Typography variant="subtitle2">
                      {item.documentExpirationDate
                        ? moment(item.documentExpirationDate).format(
                            DATETIME_FORMAT2
                          )
                        : t("Docs.Admin.NoInfo")}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {isUpdate && (
                      <Tooltip title={t("Docs.Admin.TooltipEdit")}>
                        <Button
                          color="info"
                          onClick={() => {
                            setIsEdit(true);
                            handleOpen(item.id);
                          }}
                        >
                          <ImageCustom
                            src={IconEdit}
                            width="24px"
                            height="24px"
                          />
                        </Button>
                      </Tooltip>
                    )}

                    <Tooltip
                      title={t("Docs.Admin.TooltipViewDetail")}
                      sx={{ margin: "0 20px" }}
                    >
                      <Button
                        color="info"
                        onClick={() => {
                          setIsEdit(false);
                          handleOpen(item.id);
                        }}
                      >
                        <ImageCustom
                          src={IconInfo}
                          width="24px"
                          height="24px"
                        />
                      </Button>
                    </Tooltip>

                    {isDelete && (
                      <Tooltip title={t("Docs.Admin.TooltipDelete")}>
                        <Button
                          color="info"
                          onClick={() => {
                            toggleConfirmModal({
                              title: t("Docs.Admin.ConfirmDeleteTitle"),
                              content: t("Docs.Admin.ConfirmDeleteContent"),
                              onSubmit: async () => {
                                deleteDoc.request(
                                  DocsService.deleteDoc(item.id).then(res => {
                                    if (res.success) {
                                      toggleMessage({
                                        type: "success",
                                        message:
                                          res?.message ??
                                          t("Docs.Admin.UpdateSuccess")
                                      });
                                      fetchData(getPage.pagging?.currentPage);
                                    } else {
                                      toggleMessage({
                                        type: "error",
                                        message:
                                          res?.error ??
                                          t("Docs.Admin.CommonError")
                                      });
                                    }
                                  })
                                );
                              },
                              submitText: t("Docs.Admin.ConfirmDeleteSubmit"),
                              type: "warning-red"
                            });
                          }}
                        >
                          <ImageCustom
                            src={IconDelete}
                            width="24px"
                            height="24px"
                          />
                        </Button>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {getPage.pagging && getPage.pagging.totalPages > 1 && (
            <Box
              sx={{
                margin: "12px 18px",
                display: "flex",
                justifyContent: "end",
                alignItems: "center"
              }}
            >
              <Typography>
                {1 + 10 * (getPage.pagging.currentPage - 1)} -{" "}
                {getPage.pagging.currentPage * 10 < getPage.pagging.totalCount!
                  ? getPage.pagging.currentPage * 10
                  : getPage.pagging.totalCount!}{" "}
                / {getPage.pagging.totalCount}
              </Typography>
              <Pagination
                count={getPage.pagging.totalPages}
                showFirstButton={getPage.pagging.totalPages > 2}
                showLastButton={getPage.pagging.totalPages > 2}
                shape="rounded"
                page={getPage.pagging.currentPage ?? 0}
                onChange={(_, v) => fetchData(v)}
              />
            </Box>
          )}

          <Modal open={open} onClose={() => setOpen(false)}>
            <Box sx={style} onClick={() => (isEdit ? {} : setOpen(false))}>
              <Box
                sx={{
                  backgroundColor: "white",
                  width: "55vw",
                  padding: "68px",
                  height: "auto",
                  margin: "auto"
                }}
              >
                {getSingle.loading ? (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <CircularProgress />
                    <Typography marginLeft="18px">
                      {t("Docs.Admin.LoadingData")}
                    </Typography>
                  </Box>
                ) : (
                  getSingle.data &&
                  (isEdit ? (
                    <CreateDocumentPopup
                      handleClose={() =>
                        isEdit ? setOpen(false) : setOpenCreate(false)
                      }
                      handleOnSubmit={createOnSubmit}
                      isLoading={isEdit ? update.loading : create.loading}
                      editItem={getSingle.data}
                      documentTypes={documentTypes ?? []}
                      documentTypeId={getSingle.data.documentTypeId}
                    />
                  ) : (
                    <DocsContent item={getSingle.data} />
                  ))
                )}
              </Box>
            </Box>
          </Modal>
        </Box>
      ) : (
        <Box>
          <Box display="flex">
            <Box flexGrow={1} />

            {isViewType && (
              <Button
                variant="outlined"
                color="info"
                sx={{ marginRight: "15px" }}
                endIcon={<KeyboardArrowDownRounded />}
                onClick={() => setOpenDocType(true)}
              >
                {t("Docs.Admin.ButtonDocumentType")}
              </Button>
            )}
            {isCreate && (
              <Button
                startIcon={<AddRounded />}
                variant="contained"
                onClick={() => handleCreateOpen()}
              >
                {t("Docs.Admin.ButtonCreateDocument")}
              </Button>
            )}
          </Box>
          <Box className="empty-box">
            <ImageCustom src={EmptyProject} width="auto" height="170px" />
            <Typography variant="h6" marginTop="28px">
              {t("Docs.Admin.EmptyTitle")}
            </Typography>
          </Box>
        </Box>
      )}
      <Modal open={openCreate} onClose={() => setOpenCreate(false)}>
        <Box sx={style}>
          {documentTypes && documentTypes.length > 0 ? (
            <CreateDocumentPopup
              handleClose={() => setOpenCreate(false)}
              handleOnSubmit={createOnSubmit}
              isLoading={create.loading}
              documentTypes={documentTypes}
              documentTypeId={getSingle.data?.documentTypeId}
            />
          ) : (
            <CreateDocumentTypePopup
              handleClose={handleCloseCreateDocumentType}
            />
          )}
        </Box>
      </Modal>

      <Modal open={openDocType} onClose={() => setOpenDocType(false)}>
        <Box sx={style}>
          {documentTypes && (
            <DocumentTypePopup
              data={documentTypes}
              style={boxStyle}
              handleClose={handleCloseDocumentType}
              isCreate={isCreateType}
              isEdit={isUpdateType}
              isDelete={isDeleteType}
            />
          )}
        </Box>
      </Modal>
    </Wrapper>
  );
};

export default DocsAdminView;
